/*
 * Copyright (c) 2026. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* planitem-invulling.component.ts
* 17-12-2025 - Jelmer Jellema - Spin in het Web B.V.
* Gegenereerd met Junie (AI), 17-12-2025
*/

import {Component, Input, OnChanges, SimpleChanges, inject} from '@angular/core';
import {Planitem} from '../../models/planitem';
import {hash} from '../../app/types';
import {Personeel} from '../../models/personeel';
import {PersoneelPlanitem} from '../../models/personeel_planitem';
import {Leverancier} from '../../models/leverancier';
import {FiltertoolInstantie} from '../../services/filtertool/filtertool-instantie';
import {ApiService} from '../../services/api/api.service';
import moment from 'moment';
import {BaseComponent} from '../../components/basecomponent';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

interface Personeelfit {
    p: Personeel;
    functionfit: number;
    leverancierfit: number;
    certificeringVerlopen: boolean; //algemene certificering (raamovereenkomst) of die voor een functie van deze dienst verlopen op de dienstdatum
}

@Component({
    standalone: true,
    selector: 'planitem-invulling',
    templateUrl: './planitem-invulling.component.html',
    styleUrls: ['./planitem-invulling.component.scss'],
    imports: [CommonModule, FormsModule, NgSelectModule]
})
export class PlanitemInvullingComponent extends BaseComponent implements OnChanges {
    /**
     * Het planitem dat wordt bewerkt.
     */
    @Input() record: Planitem;

    /**
     * Of het record alleen-lezen is.
     */
    @Input() readonly: boolean = false;

    /**
     * Indicatie of de ingelogde gebruiker een leverancier is.
     */
    @Input() isLeverancier: boolean = false;

    /**
     * Indicatie of de ingelogde gebruiker een uitlener (externe WM) is.
     */
    @Input() isUitlener: boolean = false;

    /**
     * Geeft true als de gebruiker een leverancier of uitlener is.
     */
    get isExterneBewerker(): boolean {
        return this.isLeverancier || this.isUitlener;
    }

    /**
     * Toegangsvlag voor volledige leesrechten op personeel.
     */
    @Input() volLezen: boolean = false;

    /**
     * Of personeel mag worden aangepast.
     */
    @Input() magPersoneelEdit: boolean = false;

    /**
     * Lookups voor personeel en leveranciers.
     */
    @Input() lookups: hash<any> = {};

    /**
     * Lookup-hashes per type.
     */
    @Input() lookupHashes: hash<hash<any>> = {};

    /**
     * Schakelaar voor het negeren van actieve filters.
     */
    @Input() negeerFilter: boolean = false;

    /**
     * Alle planitems per datum voor overlapcontrole.
     */
    @Input() allitemsPerdate: hash<Planitem[]> = {};

    /**
     * De actuele filtertool-instantie.
     */
    @Input() filtertool: FiltertoolInstantie | null = null;

    /**
     * Css-selector van de container waarin de keuzelijst van dit component geplaatst wordt. Komt van
     * het scherm (de body van het window), zodat de lijst niet wordt afgeknipt door de scrollende
     * inhoud en toch met het window meeschuift.
     */
    @Input() dropdownContainer: string = 'body';

    /**
     * De weergavelijst van personeel, inclusief fit-info.
     */
    fittedPersoneel: Personeelfit[] = [];

    /**
     * Op true bij personeelswijziging
     * @private
     */
    private gewijzigd: boolean = false;

    /**
     * Ongefilterde basislijst van personeel inclusief fit-info.
     */
    private fittedPersoneelOngefilterd: Personeelfit[] = [];

    /**
     * Gefilterde lijst zonder aanvulling van reeds gekozen personeel.
     */
    private fittedPersoneelGefilterd: Personeelfit[] = [];

    /**
     * Labels voor fit-indicatie.
     */
    readonly functionfit = {
        4: 'Voldoet aan alle functie-eisen, hoofdfuncties passen',
        3: 'Voldoet aan alle functie-eisen',
        2: 'Hoofdfunctie past, maar niet alle extra functies passen',
        1: 'Mist de juiste functies',
        0: 'Afwezig'
    };

    /**
     * De apiservice voor gebruikerscontext en aanvullende data.
     */
    readonly api = inject(ApiService);

    constructor() {
        super('debug');
    }

    /**
     * Helper voor eigenaarscherm: is dit ding dirty?
     */
    get isDirty(): boolean {
        return this.gewijzigd;
    }

    /**
     * True als de filtertool personeel heeft weggefilterd.
     */
    get personeelWeggefilterd(): boolean {
        // this.log.debug(`personeelweggefilted?`, !! this.filtertool, this.fittedPersoneelGefilterd?.length, this.fittedPersoneelOngefilterd?.length);
        if (!this.filtertool) {
            return false;
        }
        return (this.fittedPersoneelGefilterd?.length || 0) < (this.fittedPersoneelOngefilterd?.length || 0);
    }

    /**
     * Volledige herberekening van de personeelsfit en filter.
     */
    synchroniseer(): void {
        this.log.debug(`Synchroniseer personeel`, this.filtertool);
        this.fixPersoneelrows();
        this.fitPersoneel();
        this.checkPersoneelskritiek();
    }

    /**
     * Herbereken de personeelslijst o.b.v. filtertool en negeerFilter.
     */
    herberekenPersoneelFilter(): void {
        this.log.debug(`Herbereken personeelfilter`, this.fittedPersoneelGefilterd);
        const bron = this.fittedPersoneelOngefilterd || [];
        //komt te vroeg
        if (this.filtertool) {
            this.fittedPersoneelGefilterd = bron.filter(pd => this.filtertool!.filterPersoneel(pd.p));
        } else {
            this.fittedPersoneelGefilterd = [...bron];
        }

        if (!this.filtertool || this.negeerFilter) {
            this.fittedPersoneel = [...bron];
            return;
        }

        const aangevuld = [...this.fittedPersoneelGefilterd];
        let ietsToegevoegd = false;

        const gekozenIds: number[] = [];
        for (const ppi of (this.record?.personeel_planitem || [])) {
            if (ppi?.personeelvast_id) {
                gekozenIds.push(ppi.personeelvast_id);
            }
        }

        for (const pid of gekozenIds) {
            if (aangevuld.some(pd => pd.p?.id === pid)) {
                continue;
            }
            const gevonden = bron.find(pd => pd.p?.id === pid);
            if (gevonden) {
                aangevuld.push(gevonden);
                ietsToegevoegd = true;
            }
        }

        if (ietsToegevoegd || aangevuld.length) {
            aangevuld.sort((a, b) =>
                (b.leverancierfit - a.leverancierfit) || (b.functionfit - a.functionfit) || a.p.naam.localeCompare(b.p.naam)
            );
        }

        this.fittedPersoneel = aangevuld;
    }

    /**
     * Reageert op wijzigingen van inputs zoals filtertool en negeerFilter.
     * Als de ongefilterde personeelslijst al beschikbaar is, wordt de gefilterde lijst
     * direct opnieuw opgebouwd zodat de UI klopt.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['filtertool'] || changes['negeerFilter'])) {
            // Alleen herberekenen als de basislijst al is opgebouwd
            if (this.fittedPersoneelOngefilterd && this.fittedPersoneelOngefilterd.length >= 0) {
                this.herberekenPersoneelFilter();
            }
        }
    }

    /**
     * Check overlap en afwezigheid voor personeel.
     */
    checkPersoneelskritiek(): void {
        this.record.resetKritiek();
        if (this.record.startdate?.length && this.record.starttime?.length) {
            let checkdatum = moment(this.record.startdatetime).subtract(1, 'day');
            const checkitems = [];
            for (let i = 0; i < 3; i++) {
                const checkdatumstr = checkdatum.format('YYYY-MM-DD');
                if (this.allitemsPerdate[checkdatumstr]) {
                    checkitems.push(...this.allitemsPerdate[checkdatumstr]);
                }
                checkdatum.add(1, 'day');
            }
            this.record.checkPersoneeloverlap(checkitems);
        }
    }

    /**
     * Zorg dat er voldoende personeelsregels aanwezig zijn.
     */
    fixPersoneelrows(): void {
        this.log.debug(`Fix personeelrows`, this.record.personeel_planitem);
        if (!this.record?.personeel_planitem) {
            return;
        }
        this.record.personeel_planitem = this.record.personeel_planitem.filter(ppi => ppi.personeelvast_id);
        //de rijen worden in de template getrackt op trackerId, de unieke id die elk model zelf heeft
        if (this.magPersoneelEdit && (!this.readonly)) {
            while (this.record.personeel_planitem.length < this.record.capacity) {
                this.record.personeel_planitem.push(PersoneelPlanitem.createNew(this.record, {}));
            }
            if ((!this.isExterneBewerker) && this.record.personeel_planitem.every(ppi => ppi.personeelvast_id)) {
                this.record.personeel_planitem.push(PersoneelPlanitem.createNew(this.record, {}));
            }
        }
    }

    /**
     * Controleer of een personeelslid al gekozen is in een andere regel.
     */
    noDuplicatePersoneel(personeels_id: number, forPPI: PersoneelPlanitem): boolean {
        return !this.record.personeel_planitem.some(ppi => ppi !== forPPI && ppi.personeelvast_id === personeels_id);
    }

    /**
     * Bereken de fit van personeel t.o.v. gekozen functies en leverancier.
     */
    fitPersoneel(): void {
        this.log.debug(`Fit personeel`, this.lookupHashes['personeel']);
        const mainfunction = this.record.mainFunction;
        const extrafunctions = this.record.extraFunctions;

        this.record.setPersoneel(<hash<Personeel>>this.lookupHashes['personeel']);
        this.record.setFunctielookup(this.lookupHashes['functions']); //zodat de kritiek functienamen kan noemen
        this.record.setKritiekLeverancierfilter(this.isLeverancier ? this.api.userdata.leverancier_id : null); //een leverancier ziet alleen kritiek over zijn eigen mensen

        if (!this.record.project || !mainfunction) {
            this.log.debug(`Geen project of functie gespecificeerd, geen fit`);
            return;
        }

        const nieuw: Personeelfit[] = [];
        for (const p of <Personeel[]>this.lookups['personeel']) {
            if (!p.actief) {
                continue;
            }
            if (p.werkmaatschappij_id !== this.record.project.werkmaatschappij_id && (!this.record.personeel_planitem.some(ppi => ppi.personeelvast_id === p.id))) {
                this.log.debug(`Personeel ${p.naam} is niet gekoppeld aan project ${this.record.project.naam}, geen fit`);
                continue;
            }

            if (this.isExterneBewerker && p.groep !== 'Extern' && p.werkmaatschappij_id !== this.api.werkmaatschappij.id) {
                continue;
            }

            const pdata: Personeelfit = {
                p,
                functionfit: 0,
                leverancierfit: 0,
                certificeringVerlopen: this.certificeringVerlopen(p)
            };
            nieuw.push(pdata);
            const hasAllExtra = extrafunctions.every(fid => p.heeftFunctie(fid));

            if (this.record.startdate?.length && p.afwezigOp(this.record.startdate, this.record.starttime || '00:00', this.record.endtime || '00:00')) {
                pdata.functionfit = 0;
            } else if (!this.record.project.factureerbaar) {
                pdata.functionfit = 4;
            } else if (p.heeftFunctie(mainfunction, true)) {
                pdata.functionfit = hasAllExtra ? 4 : 2;
            } else if (p.heeftFunctie(mainfunction)) {
                pdata.functionfit = hasAllExtra ? 3 : 2;
            } else {
                pdata.functionfit = 1;
            }

            if (this.isLeverancier && p.leverancier_id === this.api.userdata.leverancier_id) {
                pdata.leverancierfit = 1;
            }
        }

        nieuw.sort((a, b) =>
            b.leverancierfit - a.leverancierfit || b.functionfit - a.functionfit || a.p.naam.localeCompare(b.p.naam)
        );

        this.fittedPersoneelOngefilterd = nieuw;
        this.herberekenPersoneelFilter();
    }

    /**
     * Geeft true als de certificering van dit personeelslid op de dienstdatum verlopen is: de algemene
     * certificering, of die voor een van de functies van deze dienst die hij wél mag doen. Mist hij de
     * functie zelf, dan is dat een functieprobleem en geen certificeringsprobleem. Net als bij de
     * kritiek telt dit alleen op een factureerbaar project.
     * @param p
     */
    certificeringVerlopen(p: Personeel): boolean {
        if (!(this.record.project?.factureerbaar && this.record.startdate?.length)) {
            return false;
        }
        return (!p.certificeringGeldigOp(this.record.startdate))
            || this.record.functions.some(fid => p.heeftFunctie(fid) && (!p.heeftFunctieGeldigOp(fid, this.record.startdate)));
    }

    /**
     * Zoek een personeelslid op id.
     */
    personeel(id: number): Personeel {
        return <Personeel>(this.lookupHashes && this.lookupHashes['personeel'] && this.lookupHashes['personeel'][id]) || null;
    }

    leverancierNaam(id: number): string {
        const lev = this.lookups?.['leverancier']?.find((l: any) => l.id === id);
        return lev ? lev.naam : '';
    }

    /**
     * Zoek fit-informatie voor een personeelslid.
     */
    personeelfit(id: number): Personeelfit {
        return this.fittedPersoneel.find(p => p.p.id === id) || null;
    }

    /**
     * Zoek een leverancier op id.
     */
    leverancier(id: number): Leverancier {
        return <Leverancier>(this.lookupHashes && this.lookupHashes['leverancier'] && this.lookupHashes['leverancier'][id]) || null;
    }

    /**
     * Behandel personeelswijziging.
     */
    kiesPersoneel(ppi: PersoneelPlanitem): void {
        if (ppi && ppi.personeelvast_id) {
            const p = this.personeel(ppi.personeelvast_id);
            ppi.leverancier_id = p.groep === 'Personeel' || (this.isUitlener && p.werkmaatschappij_id === this.api.werkmaatschappij.id) ? null : (this.isLeverancier ? this.api.userdata.leverancier_id : p.leverancier_id);
        }
        this.personeelChange();
    }

    /**
     * Sluit de info-weergave voor een personeelsregel.
     */
    stopPPIEdit(ppi: PersoneelPlanitem): void {
        ppi._tags.info = false;
        this.personeelChange();
    }

    /**
     * Standaard verwerking na personeelswijziging.
     */
    personeelChange(): void {
        this.fixPersoneelrows();
        this.fitPersoneel();
        this.checkPersoneelskritiek();
        this.gewijzigd = true;
    }


    /**
     * Toon tekst voor een personeelslid inclusief fitlabel, en de melding als de certificering op de
     * dienstdatum verlopen is.
     */
    personeelTitle(pdata: Personeelfit | null): string {
        if (!pdata?.functionfit) {
            return '';
        }
        const certificering = pdata.certificeringVerlopen ? ' – certificering verlopen' : '';
        if (pdata.leverancierfit) {
            return `${pdata.p.naam} (L)${certificering}`;
        }
        if (pdata.p.groep === 'Extern') {
            return `${pdata.p.naam} (E)${certificering}`;
        }
        return `${this.functionfit[pdata.functionfit] ?? ''}${certificering}`;
    }

    /**
     * Geeft true als we vinden dat een leverancier het personeel niet mag zien
     * @param ppi
     */
    onzichtbaarVoorLeverancier(ppi: PersoneelPlanitem): boolean {
        return this.isLeverancier && ppi.personeelvast_id && ((!this.personeel(ppi.personeelvast_id)) || ppi.leverancier_id !== this.api.userdata.leverancier_id);
    }

    /**
     * Geeft true als we vinden dat een uitlener een ander personeelslid alleen lezen moet zien
     */
    alleenLezenVoorUitlener(ppi: PersoneelPlanitem): boolean {
        return this.isUitlener && ppi.personeelvast_id && this.personeel(ppi.personeelvast_id) && this.personeel(ppi.personeelvast_id).werkmaatschappij_id !== this.api.werkmaatschappij.id
    }

    /**
     * Check of een personeelslid van een andere werkmaatschappij is in een partner-context.
     */
    isAndereWM(id: number): boolean {
        if (!this.record.externe_werkmaatschappij) {
            return false;
        }
        if (this.isExterneBewerker) {
            return false;
        }
        const p = this.personeel(id);
        return p && p.werkmaatschappij_id !== this.api.werkmaatschappij.id;
    }

    /**
     * Geeft true als we vinden dat een leverancier alleen lezen mag worden getoond
     * @param ppi
     */
    isLeverancierReadonly(ppi: PersoneelPlanitem): boolean {
        return ppi.personeelvast_id && this.personeel(ppi.personeelvast_id)?.werkmaatschappij_id !== this.record.project?.werkmaatschappij_id && this.personeel(ppi.personeelvast_id)?.werkmaatschappij_id === this.record.externe_werkmaatschappij;
    }

    /**
     * Geeft true als de leverancier past in de keuzelijst bij de ppi
     * @param leverancier
     * @param ppi
     */
    passendeLeverancier(leverancier: Leverancier, ppi: PersoneelPlanitem): boolean {
      return (leverancier.leverancierstype === 'Inhuur' || leverancier.id === this.personeel(ppi.personeelvast_id)?.leverancier_id) && leverancier.werkmaatschappij_id === this.record.project.werkmaatschappij_id;
    }
}
